import { Refresh } from "@mui/icons-material"
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tabs,
  Typography,
} from "@mui/material"
import { map, omit } from "lodash"
import { FC, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { SstLocker } from "shared/sst"
import { useAppSelector } from "src/app/hooks"
import {
  useGetSstApiInfoQuery,
  useGetSstLockersQuery,
  useGetSstWebhooksQuery,
} from "src/features/partners/partnersApi"
import { selectSstLockers } from "src/features/partners/sstThingsSlice"
import { useSearchParamsState } from "src/hooks/useSearchParamsState"
import SaLayout from "./SaLayout"
import SstLockers from "./SaSstLockers"
import SstReservations from "./SaSstReservations"
import SstWebhooks from "./SaSstWebhooks"
import { Sl2Tab } from "./Sl2Tab"

const TabMap: Record<string, number> = {
  lockers: 0,
  reservations: 1,
  webhooks: 2,
}
const TabMapReverse: Record<number, string> = {
  0: "lockers",
  1: "reservations",
  2: "webhooks",
}

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

export const SaSstPage: FC = () => {
  const { t } = useTranslation()

  // @ts-ignore
  const qLockers = useGetSstLockersQuery({})

  const _sstLockers = useAppSelector(selectSstLockers)

  const sstLockers = useMemo(() => {
    return map(_sstLockers || [], (l) => omit(l, ["sstType"])).filter(
      Boolean,
    ) as unknown as SstLocker[]
  }, [_sstLockers])

  const qApiInfo = useGetSstApiInfoQuery({})

  const qApiInfoIsloading = qApiInfo.isLoading || qApiInfo.isFetching

  const qWebhooks = useGetSstWebhooksQuery({})

  const refresh = useCallback(() => {
    qApiInfo.refetch()
    qWebhooks.refetch()
  }, [qApiInfo, qWebhooks])

  const [tab, setTab] = useSearchParamsState("tab", TabMap.lockers + "")

  const handleTabChange = useCallback(
    (event: React.ChangeEvent<{}>, newValue: number) => {
      setTab(newValue + "")
    },
    [setTab],
  )

  return (
    <SaLayout selectedMenuItemLabelKey="sstApi">
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          width: "100%",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          flexWrap="wrap"
          gap={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "primary.main",
              }}
            >
              {t("adminApp")}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                mt: 0.5,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                lineHeight: 1.15,
              }}
            >
              {t("sst")}
            </Typography>
          </Box>
          {qApiInfoIsloading ? (
            <CircularProgress size={22} />
          ) : (
            <IconButton
              onClick={refresh}
              size="small"
              sx={{
                bgcolor: "rgba(35, 66, 133, 0.06)",
                color: "primary.main",
                "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
              }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {/* Status / config card */}
        <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
            <Typography variant="body2">{t("apiUrl")}</Typography>
            {qApiInfoIsloading ? (
              <Skeleton variant="text" width={200} height={20} />
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="caption"
                  fontFamily="monospace"
                  sx={{
                    bgcolor: "rgba(0, 0, 0, 0.1)",
                    px: 1,
                  }}
                >
                  {qApiInfo.data?.data?.sstApiUrl}
                </Typography>
                {qApiInfo.data?.data?.isOnline ? (
                  // green dot if online
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    divider={<Divider orientation="vertical" flexItem />}
                  >
                    <>
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "green",
                        }}
                      />
                      <Typography variant="caption" color="green">
                        {t("online")}
                      </Typography>
                    </>
                    <Typography variant="caption">
                      {`${t("latency")}: ${qApiInfo.data?.data?.pingLatency}ms`}
                    </Typography>
                    <Typography variant="caption">
                      {`${t("version")}: ${qApiInfo.data?.data?.version}`}
                    </Typography>
                  </Stack>
                ) : (
                  // red dot if offline
                  <>
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "red",
                      }}
                    />
                    <Typography variant="caption" color="error">
                      {t("offline")}
                    </Typography>
                  </>
                )}
              </Stack>
            )}
          </Stack>
        </Box>

        {/* Tabs rail */}
        <Box
          sx={{
            borderBottom: "1px solid",
            borderColor: "rgba(0, 0, 0, 0.08)",
            mb: 2.5,
          }}
        >
          <Tabs
            value={Number(tab)}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              "& .MuiTab-root": { minHeight: 40, py: 1 },
            }}
          >
            <Sl2Tab label={t("lockers")} />
            <Sl2Tab label={t("reservations")} />
            <Sl2Tab label={t("webhooks")} />
          </Tabs>
        </Box>

        {/* Tab content */}
        <Box sx={{ ...cardSx, overflow: "hidden" }}>
          {
            {
              webhooks: (
                <SstWebhooks
                  data={qWebhooks.data?.data}
                  isLoading={qWebhooks.isLoading}
                />
              ),
              lockers: (
                <SstLockers data={sstLockers} isLoading={qLockers.isLoading} />
              ),
              reservations: <SstReservations />,
            }[TabMapReverse[Number(tab)]]
          }
        </Box>
      </Box>
    </SaLayout>
  )
}

export default SaSstPage
